import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import AppLayout from "@/components/layout/AppLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useApplicabilityEnabled } from "@/hooks/useAssessmentApplicability";

interface RuleRow {
  version_id: string;
  version: number;
  status: string;
  profile_dimension: string;
  question_id: number;
  display_order: number;
  domain: string | null;
  title: string | null;
  applicability_type: string;
  profile_values: string[];
  infrastructure_components: string[];
  enabled: boolean;
}

/**
 * Phase 2A — read-only viewer of question applicability rules.
 * Editing is intentionally not available: rule content requires domain sign-off
 * and is loaded through controlled migrations only.
 */
const ApplicabilityRules = () => {
  const [questionnaireId, setQuestionnaireId] = useState("telecom_v2");
  const { data: enabled } = useApplicabilityEnabled();

  const { data, isLoading, error } = useQuery({
    queryKey: ["applicability-rules", questionnaireId],
    enabled: questionnaireId.trim().length > 0,
    queryFn: async (): Promise<RuleRow[]> => {
      const { data, error } = await (supabase.rpc as any)("admin_list_applicability_rules", {
        p_questionnaire_id: questionnaireId.trim(),
      });
      if (error) throw error;
      return (data ?? []) as RuleRow[];
    },
  });

  const versions = useMemo(() => {
    const map = new Map<string, RuleRow[]>();
    (data ?? []).forEach((r) => {
      const key = `v${r.version} · ${r.status} · ${r.profile_dimension}`;
      map.set(key, [...(map.get(key) ?? []), r]);
    });
    return Array.from(map.entries());
  }, [data]);

  return (
    <AppLayout
      title="Question Applicability Rules | CSV Pro Admin"
      description="Read-only viewer for versioned question applicability rule sets."
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Question Applicability Rules</h1>
          <p className="text-sm text-muted-foreground">
            Read-only. Rule content is loaded through reviewed migrations after domain sign-off.
          </p>
        </div>
        <Badge variant={enabled ? "default" : "secondary"}>
          Applicability filtering {enabled ? "ENABLED" : "DISABLED"}
        </Badge>
      </div>

      <div className="mb-4 max-w-sm">
        <Input
          value={questionnaireId}
          onChange={(e) => setQuestionnaireId(e.target.value)}
          placeholder="questionnaire id (e.g. telecom_v2)"
          aria-label="Questionnaire ID"
        />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      {versions.map(([label, rows]) => (
        <Card key={label} className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">{label}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Question</th>
                  <th className="p-2 text-left">Domain</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Service Models</th>
                  <th className="p-2 text-left">Components</th>
                  <th className="p-2 text-left">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.version_id}-${r.question_id}`} className="border-t align-top">
                    <td className="p-2">{r.display_order}</td>
                    <td className="p-2">{r.title ?? r.question_id}</td>
                    <td className="p-2">{r.domain ?? "—"}</td>
                    <td className="p-2">{r.applicability_type}</td>
                    <td className="p-2">{r.profile_values?.join(", ") || "—"}</td>
                    <td className="p-2">{r.infrastructure_components?.join(", ") || "—"}</td>
                    <td className="p-2">{r.enabled ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </AppLayout>
  );
};

export default ApplicabilityRules;
